import { json, postBiz } from '../core/coreapi';
import {
  asNumber,
  normalizeForecastingManager,
  normalizeForecastQualityIssue,
  normalizeForecastResultRow,
  normalizeWorkbench,
} from './bieforecasting.normalizers';
import {
  IForecastResultFilters,
  IForecastResultRow,
  IForecastQualityIssue,
  IForecastQualityIssueFilters,
  IForecastingManager,
  IForecastingWorkbench,
  IForecastVariableRow,
  IPagedResult,
} from './bieforecasting.models';

const SERVICE = 'bieforecasting';
const WORKBENCH_SERVICE = 'bieforecastbaseline';

export const fetchForecastingManager = async (planningcycleid: number): Promise<IForecastingManager> =>
  normalizeForecastingManager(await postBiz({ s: SERVICE, a: 'manager', pci: planningcycleid }));

export const fetchForecastingWizard = async (
  planningcycleid: number,
  _batchid: number,
): Promise<IForecastingWorkbench> =>
  normalizeWorkbench(await postBiz({
    s: WORKBENCH_SERVICE,
    a: 'workbench',
    pci: planningcycleid,
  }));

export const createForecastBatch = async (
  planningcycleid: number,
  batchname: string,
  historymonths = 60,
) => json(await postBiz({
  s: SERVICE,
  a: 'createbatch',
  pci: planningcycleid,
  nam: batchname,
  hms: historymonths,
}));

export const deleteForecastBatch = async (batchid: number) =>
  json(await postBiz({ s: SERVICE, a: 'deletebatch', bid: batchid }));

export const completeForecasting = async (planningcycleid: number, notes = '') =>
  json(await postBiz({ s: SERVICE, a: 'complete', pci: planningcycleid, nts: notes }));

export const validateBaselineIntegrity = async (batchid: number) =>
  json(await postBiz({ s: SERVICE, a: 'validatebaseline', bid: batchid }));

export const saveForecastVariables = async (batchid: number, variables: IForecastVariableRow[]) =>
  json(await postBiz({
    s: SERVICE,
    a: 'savevariables',
    bid: batchid,
    rcd: variables.map((row) => ({
      rid: row.rid ?? null,
      rix: row.rix ?? null,
      vrc: row.variablecode,
      fmd: row.forecastmethod,
      src: row.source,
      efr: row.effectivefrom || null,
      eto: row.effectiveto || null,
      grt: row.growthrate ?? null,
      fvl: row.fixedvalue ?? null,
      sea: row.seasonality ? 1 : 0,
      ovr: row.overrideReason || null,
      stp: row.stamp ?? null,
    })),
  }));

export const runMissingPeriodForecast = async (
  batchid: number,
  options: Record<string, unknown>,
) => json(await postBiz({ s: SERVICE, a: 'generate', bid: batchid, opt: options || {} }));

export const validateForecastQuality = async (batchid: number) =>
  json(await postBiz({ s: SERVICE, a: 'quality', bid: batchid }));

export const publishForecastBaseline = async (batchid: number, notes: string) =>
  json(await postBiz({ s: SERVICE, a: 'publish', bid: batchid, nts: notes || '' }));

export const fetchForecastResults = async (
  filters: IForecastResultFilters,
): Promise<IPagedResult<IForecastResultRow>> => {
  const raw: any = await postBiz({
    s: SERVICE,
    a: 'results',
    pci: filters.planningcycleid,
    bid: filters.batchid,
    vrc: filters.variablecode || null,
    pdc: filters.periodcode || null,
    sch: filters.search || null,
    sts: filters.status || null,
    pos: filters.page * filters.pageSize,
    plm: filters.pageSize,
  });
  const data = raw?.sd ?? raw ?? {};
  const rows = Array.isArray(data) ? data : data.rows ?? data.rec ?? [];
  return {
    rows: rows.map(normalizeForecastResultRow),
    rowCount: asNumber(data.rowCount ?? data.rowcount ?? data.cnt, rows.length),
    page: asNumber(data.page, filters.page),
    pageSize: asNumber(data.pageSize ?? data.pagesize, filters.pageSize),
  };
};

export const fetchForecastQualityIssues = async (
  filters: IForecastQualityIssueFilters,
): Promise<IPagedResult<IForecastQualityIssue>> => {
  const raw: any = await postBiz({
    s: SERVICE,
    a: 'qualityissues',
    pci: filters.planningcycleid,
    bid: filters.batchid,
    sev: filters.severity || null,
    sch: filters.search || null,
    pos: filters.page * filters.pageSize,
    plm: filters.pageSize,
  });
  const data = raw?.sd ?? raw ?? {};
  const rows = Array.isArray(data) ? data : data.rows ?? data.rec ?? [];
  return {
    rows: rows.map(normalizeForecastQualityIssue),
    rowCount: asNumber(data.rowCount ?? data.rowcount ?? data.cnt, rows.length),
    page: asNumber(data.page, filters.page),
    pageSize: asNumber(data.pageSize ?? data.pagesize, filters.pageSize),
  };
};
